import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';
import { formatCurrency } from '../../utils/currency';

import {
  buildUnifiedActivity,
  filterActivityByDateRange,
  getActivityReportSummary,
  getLocalDateKey,
  getRollingDateRange,
} from '../../utils/activity';

import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import '../../styles/brand-typography.css';
import './KidReports.css';


// ==================================================
// Date Helpers
// ==================================================

const formatReportDate = (dateKey) => {
  if (!dateKey) return '';

  const [year, month, day] = dateKey
    .split('-')
    .map(Number);

  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString(
    undefined,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );
};


const formatActivityDate = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString(
    undefined,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );
};


// ==================================================
// Summary Card
// ==================================================

const SummaryCard = ({
  icon,
  label,
  value,
  note,
}) => (
  <div
    className="
      rounded-xl
      border
      border-gray-200
      bg-white
      p-2.5
      shadow-sm
    "
  >
    <div className="flex items-center gap-2">
      <MyPiggyIcon name={icon} size="sm" />

      <p className="text-sm font-extrabold text-slate-700">
        {label}
      </p>
    </div>

    <p className="mt-0.5 text-lg font-extrabold text-slate-950 text-center tabular-nums">
      {value}
    </p>

    {note && (
      <p className="mt-1 text-xs font-bold leading-snug text-slate-600">
        {note}
      </p>
    )}
  </div>
);


// ==================================================
// Activity Row
// ==================================================

const ReportActivityRow = ({ activity }) => {
  const activityDate = formatActivityDate(
    activity.created_at
  );


  // --------------------------------------------------
  // Savings Activity
  // --------------------------------------------------

  if (
    activity.activity_kind === 'savings'
  ) {
    const isTransfer =
      activity.savings_activity_type ===
      'transfer';

    const isDeposit =
      activity.savings_activity_type ===
      'deposit';

    const isReturn =
      activity.savings_activity_type ===
        'cancel' ||
      activity.savings_activity_type ===
        'withdrawal';

    const icon =
      isTransfer
        ? 'move-between-goals'
        : isDeposit
        ? 'contribute-to-goal'
        : 'withdraw-from-savings';

    const amountColor =
      isReturn
        ? 'text-emerald-700'
        : isTransfer
        ? 'text-blue-600'
        : 'text-purple-700';

    return (
      <div className="report-activity-row px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 font-extrabold leading-snug text-slate-950">
              <MyPiggyIcon name={icon} size="sm" />
              {activity.display_title}
            </p>

            {activity.display_subtitle && (
              <p className="mt-1 text-sm font-bold text-slate-600">
                {activity.display_subtitle}
              </p>
            )}
          </div>

          <div className="flex-shrink-0 text-right">
            <p className={`font-extrabold tabular-nums ${amountColor}`}>
              {activity.display_sign}
              {formatCurrency(
                activity.amount_cents
              )}
            </p>

            <p className="mt-0.5 text-xs font-bold text-slate-600">
              {activity.amount_label}
            </p>
          </div>
        </div>

        <div className="report-activity-meta mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="text-sm font-bold text-slate-600 whitespace-nowrap">{activityDate}</span>
          <p className="ml-auto text-right text-sm font-bold text-slate-600">
            Available Money: <span className="font-extrabold text-slate-950 tabular-nums">{formatCurrency(activity.available_balance_after_cents)}</span>
          </p>
        </div>
      </div>
    );
  }


  // --------------------------------------------------
  // Normal Transaction
  // --------------------------------------------------

  const isIncome =
    activity.type === 'income';

  const isPending =
    activity.approval_status ===
    'pending';

  const isRejected =
    activity.approval_status ===
    'rejected';

  const transactionTitle =
    activity.note ||
    activity.category ||
    activity.subtype ||
    (isIncome
      ? 'Money Added'
      : 'Money Spent');

  return (
    <div className="report-activity-row px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 font-extrabold leading-snug text-slate-950">
            <MyPiggyIcon name={isIncome ? 'money-received' : 'spent-money'} size="sm" />
            {transactionTitle}
          </p>

          {(isPending || isRejected) && <div className="mt-1 flex flex-wrap items-center gap-2">

            {isPending && (
              <span
                className="
                  rounded-full
                  bg-yellow-100
                  px-2
                  py-0.5
                  text-xs
                  font-medium
                  text-yellow-700
                "
              >
                Waiting for approval
              </span>
            )}

            {isRejected && (
              <span
                className="
                  rounded-full
                  bg-red-100
                  px-2
                  py-0.5
                  text-xs
                  font-medium
                  text-red-700
                "
              >
                Rejected
              </span>
            )}
          </div>}
        </div>

        <p
          className={`
            flex-shrink-0
            font-extrabold tabular-nums
            ${
              isPending || isRejected
                ? 'text-gray-500'
                : isIncome
                ? 'text-emerald-700'
                : 'text-red-700'
            }
          `}
        >
          {isIncome ? '+' : '-'}
          {formatCurrency(
            activity.amount_cents
          )}
        </p>
      </div>

      <div className="report-activity-meta mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="text-sm font-bold text-slate-600 whitespace-nowrap">{activityDate}</span>
          <p className="ml-auto text-right text-sm font-bold text-slate-600">
            Available Money: <span className="font-extrabold text-slate-950 tabular-nums">{formatCurrency(activity.available_balance_after_cents)}</span>
          </p>
        </div>
    </div>
  );
};


// ==================================================
// Kid Reports Page
// ==================================================

const KidReports = () => {
  const { currentKid } = useKidAuth();

  const initialRange = getRollingDateRange(7);

  const [allActivity, setAllActivity] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [selectedRange, setSelectedRange] =
    useState('last7');

  const [reportRange, setReportRange] =
    useState(initialRange);

  const [customStart, setCustomStart] =
    useState(initialRange.startDate);

  const [customEnd, setCustomEnd] =
    useState(initialRange.endDate);

  const [customError, setCustomError] =
    useState('');


  // --------------------------------------------------
  // Browser Title
  // --------------------------------------------------

  useEffect(() => {
    document.title =
      'My Money Report | MyPiggy Kids';

    return () => {
      document.title =
        'MyPiggy Kids';
    };
  }, []);


  // --------------------------------------------------
  // Load Complete Kid Activity History
  // --------------------------------------------------

  useEffect(() => {
    if (
      currentKid?.id &&
      currentKid?.family_id
    ) {
      loadReportData();
    }
  }, [
    currentKid?.id,
    currentKid?.family_id,
  ]);


  const loadReportData = async () => {
    try {
      setLoading(true);
      setError('');

      const [
        transactions,
        savingsLedger,
        goals,
      ] = await Promise.all([
        db.getTransactions(
          currentKid.family_id,
          currentKid.id
        ),
        db.getSavingsLedger(
          currentKid.id
        ),
        db.getSavingsGoals(
          currentKid.id
        ),
      ]);

      const activity =
        buildUnifiedActivity({
          transactions:
            transactions || [],
          savingsLedger:
            savingsLedger || [],
          goals:
            goals || [],
        });

      setAllActivity(activity);

    } catch (loadError) {
      console.error(
        'Unable to load kid report:',
        loadError
      );

      setError(
        loadError?.message ||
          'Unable to load your report. Please try again.'
      );

    } finally {
      setLoading(false);
    }
  };


  // --------------------------------------------------
  // Quick Date Ranges
  // --------------------------------------------------

  const selectQuickRange = (
    rangeName,
    days
  ) => {
    setSelectedRange(rangeName);
    setCustomError('');

    const nextRange =
      getRollingDateRange(days);

    setReportRange(nextRange);
  };


  // --------------------------------------------------
  // Custom Range
  // --------------------------------------------------

  const openCustomRange = () => {
    setSelectedRange('custom');
    setCustomError('');
  };


  const applyCustomRange = () => {
    setCustomError('');

    if (!customStart || !customEnd) {
      setCustomError(
        'Choose both a start date and an end date.'
      );
      return;
    }

    if (customStart > customEnd) {
      setCustomError(
        'The start date must be before the end date.'
      );
      return;
    }

    setReportRange({
      startDate: customStart,
      endDate: customEnd,
    });
  };


  // --------------------------------------------------
  // Filter + Summary
  // --------------------------------------------------

  const reportActivity = useMemo(
    () =>
      filterActivityByDateRange(
        allActivity,
        reportRange.startDate,
        reportRange.endDate
      ),
    [
      allActivity,
      reportRange.startDate,
      reportRange.endDate,
    ]
  );


  const summary = useMemo(
    () =>
      getActivityReportSummary(
        reportActivity
      ),
    [reportActivity]
  );


  const todayKey =
    getLocalDateKey(new Date());


  // ==================================================
  // Render
  // ==================================================

  return (
    <KidLayout
      className="mypiggy-brand kid-reports"
      title="My Reports"
      avatar={currentKid?.avatar}
      showBack
    >
      <div className="space-y-3">

        {/* ========================================
            Report Header
           ======================================== */}

        <Card className="p-4">
          <div className="flex items-start gap-3">
            <MyPiggyIcon name="view-my-reports" size="md" />

            <div>
              <h1 className="text-xl font-extrabold text-slate-950">
                My Money Report
              </h1>

              <p className="mt-1 text-base font-bold leading-relaxed text-slate-600">
                See what you earned, spent, and saved—and how your Available Money changed.
              </p>
            </div>
          </div>
        </Card>


        {/* ========================================
            Date Range Selector
           ======================================== */}

        <Card className="p-4">
          <h2 className="font-extrabold text-slate-950">
            Choose a time period
          </h2>

          <div className="mt-2 grid grid-cols-3 gap-2">
            <button
              type="button"
              aria-pressed={selectedRange === 'last7'}
              onClick={() =>
                selectQuickRange(
                  'last7',
                  7
                )
              }
              className={`
                rounded-xl
                border-2
                px-2
                py-2.5
                text-sm
                font-bold
                transition
                ${
                  selectedRange === 'last7'
                    ? 'border-purple-500 bg-purple-100 text-purple-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
                }
              `}
            >
              Last 7 Days
            </button>

            <button
              type="button"
              aria-pressed={selectedRange === 'last30'}
              onClick={() =>
                selectQuickRange(
                  'last30',
                  30
                )
              }
              className={`
                rounded-xl
                border-2
                px-2
                py-2.5
                text-sm
                font-bold
                transition
                ${
                  selectedRange === 'last30'
                    ? 'border-purple-500 bg-purple-100 text-purple-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
                }
              `}
            >
              Last 30 Days
            </button>

            <button
              type="button"
              aria-pressed={selectedRange === 'custom'}
              onClick={openCustomRange}
              className={`
                rounded-xl
                border-2
                px-2
                py-2.5
                text-sm
                font-bold
                transition
                ${
                  selectedRange === 'custom'
                    ? 'border-purple-500 bg-purple-100 text-purple-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
                }
              `}
            >
              Custom
            </button>
          </div>


          {/* Custom Date Inputs */}

          {selectedRange === 'custom' && (
            <div
              className="
                mt-3
                rounded-xl
                border
                border-purple-200
                bg-purple-50
                p-3
              "
            >
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm font-extrabold text-slate-700">
                  From

                  <input
                    type="date"
                    value={customStart}
                    max={todayKey}
                    onChange={(event) =>
                      setCustomStart(
                        event.target.value
                      )
                    }
                    className="
                      mt-1.5
                      w-full
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      px-2
                      py-2
                      text-sm
                      text-slate-950
                      focus:border-purple-500
                      focus:outline-none
                      focus:ring-2
                      focus:ring-purple-200
                    "
                  />
                </label>

                <label className="text-sm font-extrabold text-slate-700">
                  To

                  <input
                    type="date"
                    value={customEnd}
                    max={todayKey}
                    onChange={(event) =>
                      setCustomEnd(
                        event.target.value
                      )
                    }
                    className="
                      mt-1.5
                      w-full
                      rounded-lg
                      border
                      border-gray-300
                      bg-white
                      px-2
                      py-2
                      text-sm
                      text-slate-950
                      focus:border-purple-500
                      focus:outline-none
                      focus:ring-2
                      focus:ring-purple-200
                    "
                  />
                </label>
              </div>

              {customError && (
                <p className="mt-3 text-sm font-medium text-red-700">
                  {customError}
                </p>
              )}

              <Button
                variant="kid"
                className="mt-3 w-full"
                onClick={applyCustomRange}
              >
                View Custom Report
              </Button>
            </div>
          )}
        </Card>


        {/* ========================================
            Loading / Error
           ======================================== */}

        {loading && (
          <Card className="p-4 text-center">
            <p className="font-medium text-gray-600">
              Loading your report...
            </p>
          </Card>
        )}

        {!loading && error && (
          <Card className="p-4 border-red-200 bg-red-50">
            <p role="alert" className="text-base font-bold text-red-700">
              {error}
            </p>

            <Button
              variant="secondary"
              className="mt-4 w-full"
              onClick={loadReportData}
            >
              Try Again
            </Button>
          </Card>
        )}


        {/* ========================================
            Report Results
           ======================================== */}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-wide text-purple-800">
                    Report Period
                  </p>

                  <h2 className="mt-1 text-base font-extrabold text-slate-950">
                    {formatReportDate(
                      reportRange.startDate
                    )}
                    {' – '}
                    {formatReportDate(
                      reportRange.endDate
                    )}
                  </h2>
                </div>

                <span
                  className="
                    rounded-full
                    bg-gray-100
                    px-3
                    py-1
                    text-xs
                    font-bold
                    text-gray-600
                  "
                >
                  {summary.activityCount}{' '}
                  {summary.activityCount === 1
                    ? 'activity'
                    : 'activities'}
                </span>
              </div>

              <div className="report-summary-grid mt-3 grid grid-cols-2 gap-2">
                <SummaryCard
                  icon="money-received"
                  label="Money Added"
                  value={formatCurrency(
                    summary.moneyAddedCents
                  )}
                />

                <SummaryCard
                  icon="spent-money"
                  label="Money Spent"
                  value={formatCurrency(
                    summary.moneySpentCents
                  )}
                />

                <SummaryCard
                  icon="contribute-to-goal"
                  label="Moved to Goals"
                  value={formatCurrency(
                    summary.movedToGoalsCents
                  )}
                />

                <SummaryCard
                  icon="do-chores"
                  label="Chore Earnings"
                  value={formatCurrency(
                    summary.choreEarningsCents
                  )}
                  note="Included in Money Added"
                />
              </div>
            </Card>


            {/* Activity List */}

            <Card className="p-4">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-950">
                <MyPiggyIcon name="transaction-history" size="sm" />
                Activity
              </h2>
              <p className="mt-1 text-sm font-bold text-slate-600">Your latest activity comes first.</p>

              {reportActivity.length === 0 ? (
                <div className="py-8 text-center">
                  <MyPiggyIcon name="transaction-history" size="lg" />

                  <p className="mt-3 font-extrabold text-slate-950">
                    No activity in this time period
                  </p>

                  <p className="mt-1 text-sm font-bold text-slate-600">
                    Try another date range to see your money history.
                  </p>
                </div>
              ) : (
                <div className="report-activity-list mt-3 -mx-4 divide-y divide-slate-200">
                  {reportActivity.map(
                    (activity) => (
                      <ReportActivityRow
                        key={
                          activity.activity_id
                        }
                        activity={activity}
                      />
                    )
                  )}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </KidLayout>
  );
};


export default KidReports;

